import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useContactPopup } from '@/hooks/useContactPopup';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(0);
  const navigate = useNavigate();
  const { openPopup } = useContactPopup();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { 
      name: 'Trademark Registration (UK)', 
      path: '/services/trademark-registration',
      description: 'Protect your brand identity with comprehensive trademark registration. Expert handling from search to IPO filing with 98% success rate. Our service includes thorough trademark searches, professional application preparation, direct submission to UK IPO, and complete monitoring until registration.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop&q=90'
    },
    { 
      name: 'Copyright Registration', 
      path: '/services/copyright-registration',
      description: 'Secure your creative works with professional copyright registration. Legal proof of ownership for books, music, art, software, and all creative content. Our service covers literary works, musical compositions, artistic creations, photographs, architectural designs, and software code.',
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800&h=500&fit=crop&q=90'
    },
    { 
      name: 'Trademark Comprehensive Search', 
      path: '/services/comprehensive-search',
      description: 'Thorough trademark search across UK IPO, Companies House, and domain databases. Identify conflicts before filing to avoid costly rejections. We analyze phonetic similarities, visual resemblances, and conceptual conflicts across all 45 trademark classes.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=500&fit=crop&q=90'
    },
    { 
      name: 'Company Formation', 
      path: '/services/company-formation',
      description: 'Fast UK limited company formation. Get incorporated within 24 hours with full Companies House compliance. Our service includes company name availability check, preparation of all statutory documents, appointment of directors and shareholders, and complete registration.',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&h=500&fit=crop&q=90'
    },
    { 
      name: 'Amazon Brand Registry', 
      path: '/services/amazon-brand-registry',
      description: 'Protect your Amazon business from counterfeiters. Trademark registration plus Brand Registry enrollment. Access proactive brand protection, counterfeit removal capabilities, control over product listings, A+ Content, Sponsored Brands advertising, and Amazon Stores.',
      image: 'https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800&h=500&fit=crop&q=90'
    }
  ];

  const handleChatNow = () => {
    try {
      // Open Zendesk chat widget - use webWidget API
      if (typeof window !== 'undefined') {
        if (window.zE) {
          // Try both methods for compatibility
          try {
            window.zE('messenger', 'open');
          } catch (e) {
            // Fallback to webWidget API
            if (window.zE.activate) {
              window.zE.activate();
            }
          }
        } else {
          // Widget not loaded yet - show friendly message
          setTimeout(() => {
            if (window.zE) {
              window.zE('messenger', 'open');
            } else {
              console.log('Please use the chat widget in the bottom-right corner');
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error opening chat:', error);
    }
  };

  return (
    <nav className={cn(
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'bg-background/95 backdrop-blur-md shadow-elegant-md' : 'bg-background'
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt="Trademark Assist" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-foreground hover:text-primary transition-colors font-medium">Home</Link>
            <Link to="/about" className="text-foreground hover:text-primary transition-colors font-medium">About Us</Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative" 
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button className="flex items-center space-x-1 text-foreground hover:text-primary transition-colors font-medium">
                <span>Services</span>
                <ChevronDown className={cn('w-4 h-4 transition-transform', servicesOpen && 'rotate-180')} />
              </button>
              
              {servicesOpen && (
                <div 
                  className="fixed left-0 right-0 top-20 bg-gradient-to-br from-[#012169] via-[#1a3a8a] to-[#C8102E] border-t border-b border-white/20 shadow-elegant-xl animate-fade-in z-50"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="grid grid-cols-12 gap-8">
                      {/* Left: Services List */}
                      <div className="col-span-3 space-y-1 bg-white rounded-xl p-4 shadow-2xl">
                        <p className="text-sm font-semibold text-gray-600 mb-3">Our Services</p>
                        {services.map((service, index) => (
                          <button
                            key={service.path}
                            onMouseEnter={() => setSelectedService(index)}
                            onClick={() => {
                              navigate(service.path);
                              setServicesOpen(false);
                            }}
                            className={cn(
                              "w-full text-left px-4 py-3 rounded-lg transition-all duration-200 font-medium",
                              selectedService === index 
                                ? "bg-gradient-to-r from-[#012169] to-[#C8102E] text-white shadow-lg scale-105" 
                                : "text-gray-700 hover:bg-gray-100 hover:shadow-sm"
                            )}
                          >
                            {service.name}
                          </button>
                        ))}
                      </div>
                      
                      {/* Center: Service Description */}
                      <div className="col-span-5 flex flex-col justify-center px-8 bg-white rounded-xl py-8 shadow-2xl">
                        <h3 className="text-3xl font-bold text-gray-900 mb-5">
                          {services[selectedService].name}
                        </h3>
                        <p className="text-base text-gray-700 leading-relaxed mb-8">
                          {services[selectedService].description}
                        </p>
                        <div>
                          <Button 
                            onClick={() => {
                              navigate(services[selectedService].path);
                              setServicesOpen(false);
                            }}
                            size="lg"
                            className="bg-gradient-to-r from-[#012169] to-[#C8102E] hover:from-[#011354] hover:to-[#a80d25] text-white shadow-lg"
                          >
                            Learn More
                            <ChevronDown className="ml-2 w-4 h-4 -rotate-90" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Right: Service Image */}
                      <div className="col-span-4">
                        <div className="relative h-80 rounded-xl overflow-hidden shadow-2xl border-4 border-white">
                          <img 
                            src={services[selectedService].image}
                            alt={services[selectedService].name}
                            className="w-full h-full object-cover transition-all duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#012169]/50 via-transparent to-[#C8102E]/30"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <Link to="/contact" className="text-foreground hover:text-primary transition-colors font-medium">Contact Us</Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button onClick={() => openPopup()} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              Register My Trademark
            </Button>
            <Button onClick={handleChatNow} className="bg-primary hover:bg-primary-dark">
              Chat Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col space-y-4">
              <Link to="/" onClick={() => setIsOpen(false)} className="text-foreground hover:text-primary transition-colors font-medium">Home</Link>
              <Link to="/about" onClick={() => setIsOpen(false)} className="text-foreground hover:text-primary transition-colors font-medium">About Us</Link>
              
              <div className="space-y-2">
                <p className="text-sm font-semibold text-muted-foreground">Services</p>
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    onClick={() => setIsOpen(false)}
                    className="block pl-4 text-foreground hover:text-primary transition-colors"
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
              
              <Link to="/contact" onClick={() => setIsOpen(false)} className="text-foreground hover:text-primary transition-colors font-medium">Contact Us</Link>
              
              <div className="flex flex-col space-y-2 pt-4 border-t border-border">
                <Button onClick={() => { openPopup(); setIsOpen(false); }} variant="outline" className="w-full border-primary text-primary">
                  Register My Trademark
                </Button>
                <Button onClick={() => { handleChatNow(); setIsOpen(false); }} className="w-full bg-primary hover:bg-primary-dark">
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
